import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  afterRender,
  computed,
  contentChild,
  effect,
  forwardRef,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { TranslocoModule } from '@ngneat/transloco';
import { provideSolvaceIcons } from '../../../svz-icons/solvace-icons';
import { ZardSelectItemComponent } from '../../../../app/shared/components/select/select-item.component';
import { ZardSelectComponent } from '../../../../app/shared/components/select/select.component';
import { SvzValidationMessageComponent } from '../../../svz-validation-message/svz-validation-message.component';
import { SVZ_LEGACY_ICON_PROVIDERS, resolveLegacyIconName } from '../../../zard-adapters/legacy-zard-icon';
import { SvzControl } from '../../svz-control';
import { ISvzChipGroupedOption, ISvzChipOption } from '../interfaces/svz-chip.interface';
import { svzDroplistListHeight } from '../../svz-droplist-list-height';
import { SVZ_OPTION_ROW_CLASSES, SvzSearchIconPosition, svzSearchBoxClasses, svzSearchIconClasses } from '../../svz-option-row.classes';
import { SvzChipAvatarComponent, SvzChipAvatarKind } from '../svz-chip-avatar.component';
import { ZardTooltipDirective } from '../../../../app/shared/components/tooltip/tooltip';
import { SvzTruncatedTooltipDirective } from '../../../svz-truncated-tooltip/svz-truncated-tooltip.directive';
import { SvzChipListNavigator } from '../svz-chip-list-navigator';
import { svzChipNextValues } from '../svz-chip-selection';
import { SvzChipTriggerFitter } from '../svz-chip-trigger-fit';

interface InternalChipGroupedOption extends ISvzChipOption {
  key: string;
}

interface InternalGroupedItem {
  type: 'option' | 'label';
  level: 1 | 2;
  group?: string;
  option?: InternalChipGroupedOption;
  path: string[];
}

export type SvzChipGroupedSearchMode = 'auto' | 'always' | 'never';

export type SvzChipGroupedGroupStyle = 'default' | 'directory';

const AVATAR_ROW_HEIGHT = 42;

@Component({
  selector: 'svz-chip-grouped',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-chip-grouped.component.html',
  // Prototype .sd-ck is primary; the Zard row variant paints every svg without a text-* class in muted-foreground and the
  // svg that ng-icon renders cannot carry one, so the wrapper colours it from the icon host (more specific than the row rule).
  // (::ng-deep: the svg is rendered by ng-icon and never gets this component's encapsulation attribute)
  styles: [
    "ng-icon[name='svcCheckC'].svz-chip-check ::ng-deep svg { color: inherit !important; width: 18px !important; height: 18px !important; }",
  ],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SvzChipGroupedComponent), multi: true }, provideSolvaceIcons()],
  viewProviders: [...SVZ_LEGACY_ICON_PROVIDERS],
  standalone: true,
  imports: [
    NgIcon,
    ZardSelectComponent,
    ZardSelectItemComponent,
    NgTemplateOutlet,
    SvzValidationMessageComponent,
    TranslocoModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    SvzChipAvatarComponent,
    ZardTooltipDirective,
    SvzTruncatedTooltipDirective,
  ],
})
export class SvzChipGroupedComponent extends SvzControl implements OnDestroy {
  readonly templateRef = contentChild(TemplateRef);

  readonly label = input<string | { text: string; icon: string } | null>(null);

  readonly icon = input('');

  readonly readonly = input<boolean>(false);

  readonly showValidation = input<boolean>(true);

  readonly readonlyStyle = input<'default' | 'chip' | 'transparent-chip'>('default');

  readonly options = input<ISvzChipGroupedOption[] | null | undefined>(undefined);

  private readonly syncOptions = effect(
    () => {
      this._groups = this.options() ?? [];
      this.groupedOptionItemsList = this.flattenGroups(this._groups);
      this.filterOptions(this.searchText);
      // Same OnPush note as `svz-chip`: options that arrive after the first render must re-project.
      this.cdr.markForCheck();
      this.selectionVersion.update((version) => version + 1);
    },
    { allowSignalWrites: true }
  );

  private _groups: ISvzChipGroupedOption[] = [];

  readonly type = input<'single' | 'multiple'>('multiple');

  readonly appearance = input<'default' | 'primary'>('default');

  readonly inputId = input('');

  /**
   * Additive. `design-system` draws the prototypes' filter field (`.lbl-field` 13px/400 `#3a3a3a` 6px above the control,
   * `.sd-trg` 44px radius 8 with the primary border when open, `#9aa0ac` placeholder, `#8a90a6` chevron); `default`
   * keeps today's rendering for every existing consumer.
   */
  readonly fieldAppearance = input<'default' | 'design-system'>('default');
  readonly searchIconPosition = input<SvzSearchIconPosition>('start');

  readonly placeholder = input('');

  readonly fallbackTextValue = input('');

  readonly truncateTextValue = input<boolean>(false);

  readonly collapsedDisplaying = input<boolean>(true);

  readonly loading = input<boolean>(false);

  readonly searchMode = input<SvzChipGroupedSearchMode>('auto');

  readonly groupStyle = input<SvzChipGroupedGroupStyle>('default');

  readonly searchMinOptions = input<number>(10);

  protected readonly collapsedGroups = signal<ReadonlySet<string>>(new Set<string>());

  /** Fixed row height of the virtualized list (px); group labels and options share it (42 with an avatar). */
  readonly virtualItemSize = input<number>(36);

  /** Rows visible before the list scrolls. */
  readonly virtualMaxRows = input<number>(8);

  /** Marker in front of every option and inside the trigger chips (`area` = square tinted pin). */
  readonly optionAvatar = input<'none' | SvzChipAvatarKind>('none');

  /** `checkbox` = Zard checkbox; `check` = prototype circle-check on the selected row only. */
  readonly selectionIndicator = input<'checkbox' | 'check'>('checkbox');
  protected readonly optionTooltipEnabled = computed(() => !this.templateRef() && this.selectionIndicator() === 'check');

  /** pt-BR key of the search box placeholder (defaults to `Pesquisar`). */
  readonly searchPlaceholder = input<string>('');

  private readonly zSelect = viewChild(ZardSelectComponent);
  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  private readonly renderedItems = viewChildren(ZardSelectItemComponent);
  private readonly triggerValue = viewChild<ElementRef<HTMLElement>>('triggerValue');

  /** Prototype `fit()`: after every render (and on resize) hide the chips that overflow the trigger line into `+N`. */
  private readonly triggerFitter = new SvzChipTriggerFitter();
  private readonly fitTriggerAfterRender = afterRender(() => this.triggerFitter.fit(this.triggerValue()?.nativeElement));

  /** Same virtualized, open-on-demand list as `svz-chip` (see the note there); group labels are plain rows. */
  protected readonly optionsRevealed = signal(false);

  /** Bumped on every value change so the rows' `isSelected` computed (fed through `selectedValue`) re-evaluates. */
  private readonly selectionVersion = signal(0);

  protected readonly selectedKeySet = computed(() => {
    this.selectionVersion();
    return new Set(this.selectedOptionKeys);
  });

  private readonly revealOptionsOnOpen = effect(
    () => {
      if (!this.zSelect()?.isOpen()) {
        return;
      }
      this.optionsRevealed.set(true);
      setTimeout(() => {
        this.viewport()?.checkViewportSize();
        this.navigator.focusInitial();
      });
    },
    { allowSignalWrites: true }
  );

  private readonly wiredItems = new WeakSet<object>();

  // `setSelectHost` writes the item's `select` signal, hence `allowSignalWrites` (see `svz-chip`).
  private readonly wireRenderedItems = effect(
    () => {
      for (const item of this.renderedItems()) {
        this.wireItem(item);
      }
    },
    { allowSignalWrites: true }
  );

  private wireItem(item: Pick<ZardSelectItemComponent, 'setSelectHost' | 'zValue'>): void {
    if (this.wiredItems.has(item)) {
      return;
    }
    this.wiredItems.add(item);
    item.setSelectHost({
      selectedValue: () => {
        this.selectionVersion();
        return this.selectedOptionKeys;
      },
      selectItem: (key: string) => this.selectByKey(key),
      navigateTo: () => this.navigator.setActiveByKey(item.zValue()),
    });
  }

  protected readonly navigator = new SvzChipListNavigator<InternalGroupedItem>({
    rows: (): readonly InternalGroupedItem[] => this.visibleGroupedOptionItemsList,
    keyOf: (item): string | null => item.option?.key ?? null,
    isSelectable: (item): boolean => item.type === 'option' && !!item.option,
    selectedKeys: (): readonly string[] => this.selectedOptionKeys,
    viewport: (): CdkVirtualScrollViewport | undefined => this.viewport(),
    select: (item): void => {
      if (item.option) {
        this.selected(item.option);
      }
    },
  });

  protected searchText = '';
  protected groupedOptionItemsList: InternalGroupedItem[] = [];
  protected filteredGroupedOptionItemsList: InternalGroupedItem[] = [];

  protected readonly trackByRow = (index: number, item: InternalGroupedItem): string => item.option?.key ?? `label-${index}-${item.group ?? ''}`;

  protected get rowHeight(): number {
    return this.optionAvatar() === 'none' ? this.virtualItemSize() : Math.max(this.virtualItemSize(), AVATAR_ROW_HEIGHT);
  }

  protected get listHeight(): number {
    const rowsHeight = Math.min(this.visibleGroupedOptionItemsList.length, this.virtualMaxRows()) * this.rowHeight;
    return svzDroplistListHeight(rowsHeight, this.panelAppearance, this.showSearchInput);
  }

  protected readonly searchBoxClasses = computed(() => svzSearchBoxClasses(this.searchIconPosition()));
  protected readonly searchIconClasses = computed(() => svzSearchIconClasses(this.searchIconPosition()));

  /** Prototype `.sd-pop-dir` (340px, area / location directories) vs the plain `.sd-pop` (240px). */
  protected get panelAppearance(): 'ds' | 'ds-directory' {
    return this.isDirectory ? 'ds-directory' : 'ds';
  }

  protected get isDesignSystemField(): boolean {
    return this.fieldAppearance() === 'design-system';
  }

  protected get wrapperClasses(): string {
    return this.isDesignSystemField ? 'flex w-full flex-col gap-1.5' : 'flex w-full flex-col gap-2';
  }

  /** AB#76241 the empty message is still a flex item, so the design-system column added a second 6px gap under every field. */
  protected get validationClasses(): string {
    return this.isDesignSystemField ? 'empty:hidden' : '';
  }

  protected get labelClasses(): string {
    return this.isDesignSystemField ? 'inline-flex items-center gap-1 text-[13px] font-normal text-[#3a3a3a]' : 'inline-flex items-center gap-1 text-sm font-medium text-default';
  }

  /** Prototype `.sd-trg`: 44px, `padding:5px 10px`, `.sd.open .sd-trg{ border-color:primary }`, no focus halo, grey chevron, `.sd-disabled .sd-trg` grey. */
  protected get selectHostClasses(): string {
    return this.isDesignSystemField
      ? 'w-full [&_button]:py-[5px] [&_button]:px-2.5 [&[data-state=open]_button]:border-primary [&_button]:focus-visible:ring-0 [&_button]:focus-visible:border-primary [&_button:disabled]:bg-[#f4f5f7] [&_svg]:text-[#8a90a6] [&_svg]:opacity-100'
      : 'w-full';
  }

  protected get placeholderClasses(): string {
    return this.isDesignSystemField ? 'truncate text-[#9aa0ac]' : 'text-muted-foreground truncate';
  }

  protected get triggerChipClasses(): string {
    return this.isDesignSystemField
      ? 'inline-flex shrink-0 items-center gap-1.5 rounded-[8px] bg-[#eef0f4] py-1 pr-2 text-[13px] text-[#0d122f]'
      : 'inline-flex shrink-0 items-center gap-1.5 rounded-[8px] bg-[#eef0f4] py-0.5 pr-2 text-[13px] text-[#0d122f]';
  }

  protected get useCheckbox(): boolean {
    return this.type() === 'multiple' && this.selectionIndicator() === 'checkbox';
  }

  protected get rowClass(): string {
    const baseClass = `mb-0 w-full pr-2.5 ${SVZ_OPTION_ROW_CLASSES}`;
    return this.selectionIndicator() === 'check' ? `${baseClass} [&>span:not(.truncate)]:hidden` : baseClass;
  }

  protected get canRemoveFromTrigger(): boolean {
    return !this.disabled() && !this.readonly() && !this.loading();
  }

  protected get showSearchInput(): boolean {
    const mode = this.searchMode();
    if (mode === 'never') return false;
    if (mode === 'always') return true;
    return this.groupedOptionItemsList.filter((item) => item.type === 'option').length > this.searchMinOptions();
  }

  protected get isDirectory(): boolean {
    return this.groupStyle() === 'directory';
  }

  /** Directory style hides the options (and nested groups) of every collapsed group; the default style shows everything. */
  protected get visibleGroupedOptionItemsList(): InternalGroupedItem[] {
    if (!this.isDirectory) return this.filteredGroupedOptionItemsList;
    const collapsed = this.collapsedGroups();
    return this.filteredGroupedOptionItemsList.filter((item) => !item.path.some((group) => collapsed.has(group)));
  }

  protected isGroupCollapsed(group: string | undefined): boolean {
    return !!group && this.collapsedGroups().has(group);
  }

  protected toggleGroup(group: string | undefined): void {
    if (!group) return;
    this.collapsedGroups.update((collapsed) => {
      const next = new Set(collapsed);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
    this.navigator.reset();
  }

  protected get labelText(): string {
    const label = this.label();
    return typeof label === 'string' ? label : (label?.text ?? '');
  }

  protected get resolvedLabelIcon(): string | null {
    const label = this.label();
    const icon = typeof label === 'string' ? '' : label?.icon;
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get resolvedPrefixIcon(): string | null {
    const icon = this.icon();
    return icon ? resolveLegacyIconName(icon) : null;
  }

  protected get selectedOptions(): InternalChipGroupedOption[] {
    const values = this.normalizeValue(this.control.value);
    return this.groupedOptionItemsList
      .filter((item) => item.type === 'option' && item.option)
      .map((item) => item.option as InternalChipGroupedOption)
      .filter((option) => values.some((value) => value === option.value));
  }

  protected get selectedOptionKeys(): string[] {
    return this.selectedOptions.map((option) => option.key);
  }

  protected get visibleReadonlyOptions(): InternalChipGroupedOption[] {
    return this.collapsedDisplaying() ? this.selectedOptions.slice(0, 2) : this.selectedOptions;
  }

  protected get undisplayedSelectedOptions(): number {
    return Math.max(0, this.selectedOptions.length - this.visibleReadonlyOptions.length);
  }

  protected get selectedOptionsText(): string {
    if (!this.selectedOptions.length) return this.fallbackTextValue() || '';
    return this.selectedOptions.map((option) => this.renderOptionText(option)).join(', ');
  }

  selected(option: ISvzChipOption): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const currentValues = this.normalizeValue(this.control.value);
    const nextValues = svzChipNextValues(this.type(), currentValues, option.value);

    const modelValue = this.type() === 'single' ? (nextValues[0] ?? null) : nextValues;

    this.control.setValue(modelValue, { emitEvent: false });
    this.value = modelValue;
    this.onChange(modelValue);
    this.onTouched();
    this.cdr.markForCheck();
    this.selectionVersion.update((version) => version + 1);
  }

  protected removeOption(option: ISvzChipOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.canRemoveFromTrigger) {
      this.selected(option);
    }
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);
    this.selectionVersion.update((version) => version + 1);
  }

  openOptions(): void {}

  closeOptions(options: { fromOnDestroy?: boolean } = {}): void {
    if (!options.fromOnDestroy) {
      this.onTouched();
    }
  }

  forceFocusItemWhenDownPressed(_event: unknown): void {}

  override ngOnDestroy(): void {
    this.triggerFitter.disconnect();
    super.ngOnDestroy();
  }

  filterOptions(value: string | null): void {
    this.searchText = value ?? '';
    this.navigator.reset();
    const normalized = this.searchText.trim().toLowerCase();

    if (!normalized) {
      this.filteredGroupedOptionItemsList = [...this.groupedOptionItemsList];
      return;
    }

    const results: InternalGroupedItem[] = [];
    let lastLabelLevel1: InternalGroupedItem | null = null;
    let lastLabelLevel2: InternalGroupedItem | null = null;

    for (const item of this.groupedOptionItemsList) {
      if (item.type === 'label') {
        if (item.level === 1) {
          lastLabelLevel1 = item;
          lastLabelLevel2 = null;
        } else {
          lastLabelLevel2 = item;
        }
        continue;
      }

      if (
        !String(item.option?.label ?? '')
          .toLowerCase()
          .includes(normalized)
      ) {
        continue;
      }

      if (lastLabelLevel1 && !results.includes(lastLabelLevel1)) {
        results.push(lastLabelLevel1);
      }
      if (lastLabelLevel2 && !results.includes(lastLabelLevel2)) {
        results.push(lastLabelLevel2);
      }
      results.push(item);
    }

    this.filteredGroupedOptionItemsList = results;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filterOptions(value);
  }

  protected onListKeydown(event: KeyboardEvent): void {
    this.navigator.handleKeydown(event);
  }

  onZardSelectionChange(selection: string | string[]): void {
    const nextKeys = Array.isArray(selection) ? selection : [selection];
    const selectedOptions = this.groupedOptionItemsList
      .filter((item) => item.type === 'option' && item.option && nextKeys.includes(item.option.key))
      .map((item) => item.option as InternalChipGroupedOption);

    const modelValue = this.type() === 'single' ? (selectedOptions[0]?.value ?? null) : selectedOptions.map((option) => option.value);

    this.control.setValue(modelValue, { emitEvent: false });
    this.value = modelValue;
    this.onChange(modelValue);
    this.onTouched();
    this.selectionVersion.update((version) => version + 1);
  }

  protected renderOptionText(option: ISvzChipOption | null | undefined): string {
    return String(option?.label ?? '');
  }

  protected pictureOf(option: ISvzChipOption): string | null {
    const data = option.data as { picture?: string | null } | undefined;
    return data?.picture ?? null;
  }

  private selectByKey(key: string): void {
    const item = this.groupedOptionItemsList.find((entry) => entry.option?.key === key);
    if (item?.option) {
      this.selected(item.option);
    }
  }

  private normalizeValue(value: unknown): Array<string | number> {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    return Array.isArray(value) ? value : [value as string | number];
  }

  private flattenGroups(groups: ISvzChipGroupedOption[], level: 1 | 2 = 1): InternalGroupedItem[] {
    const items: InternalGroupedItem[] = [];
    let optionIndex = 0;

    const appendGroup = (group: ISvzChipGroupedOption, nextLevel: 1 | 2, ancestors: string[]): void => {
      items.push({ type: 'label', level: nextLevel, group: group.group, path: ancestors });
      const path = [...ancestors, group.group];

      for (const option of group.options) {
        if (this.isGroupedOption(option)) {
          appendGroup(option, 2, path);
          continue;
        }

        items.push({
          type: 'option',
          level: nextLevel,
          path,
          option: {
            ...option,
            key: `grouped-chip-${optionIndex++}`,
          },
        });
      }
    };

    for (const group of groups) {
      appendGroup(group, level, []);
    }

    return items;
  }

  private isGroupedOption(option: ISvzChipGroupedOption | ISvzChipOption): option is ISvzChipGroupedOption {
    return Array.isArray((option as ISvzChipGroupedOption).options);
  }
}
